import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell } from "lucide-react";

// Minimal in-app notification inbox — this codebase has no existing
// email/WhatsApp/notification system to hook into (see
// backend/lead_assignment.py's module docstring), so this is a from-
// scratch, deliberately small "did a lead get assigned to me" indicator:
// a bell with an unread count, polled every 30s, and a short list you can
// mark read by clicking. It reads backend/lead_assignment.py's
// `notifications` collection via GET/POST /api/lead-assignment/notifications*.
export default function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-assignment/notifications", { params: { limit: 15 } });
      setItems(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch { /* not every role can read this / not configured yet — fine, just stay quiet */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) load();
  };

  const openNotification = async (n) => {
    if (!n.read) {
      try { await api.post(`/lead-assignment/notifications/${n.id}/read`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) nav(n.link);
    else if (n.lead_id) nav("/crm/indiamart");
  };

  const markAllRead = async () => {
    try { await api.post("/lead-assignment/notifications/read-all"); } catch { /* ignore */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-slate-600 hover:text-slate-900" data-testid="notification-bell-btn">
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] leading-none rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="notification-unread-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-700">Notifications</span>
          {unreadCount > 0 && (
            <button type="button" className="text-[11px] text-blue-600 hover:underline" onClick={markAllRead} data-testid="notification-mark-all-read-btn">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto scroll-thin">
          {items.length === 0 && <div className="text-xs text-slate-500 italic px-3 py-4 text-center">No notifications yet.</div>}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openNotification(n)}
              className={`w-full text-left px-3 py-2 text-xs border-b border-slate-50 hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/50"}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex items-start gap-1.5">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">{n.title}</div>
                  <div className="text-slate-500 mt-0.5 line-clamp-2">{n.body}</div>
                  <div className="text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
